import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSplitOrderInvoiceLineItemsTableState } from './useSplitOrderInvoiceLineItemsTableState';
import TableTop from '../../../core/table/TableTop';
import TableList from '../../../core/table/TableList';
import TableBody from '../../../core/table/TableBody';
import TablePaging from '../../../core/table/TablePaging';
import TableHead from '../../../core/table/TableHead';
import { ngDelegate } from '../../../core/services/ng.delegate';
import { useSplitOrderInvoiceLineItemsTableInit } from './useSplitOrderInvoiceLineItemsTableInit';
import { useSplitOrderInvoiceLineItemsData, useSplitOrderInvoicesDetailModel } from './useSplitOrderInvoicesDetailData';
import { useSplitInvoiceLineItemDispensesData } from './useSplitInvoiceLineItemDispensesData';
import { useSelectRow } from '../../../core/table/hooks/useSelectRow';
import FlyOutMenu from '../../../core/components/FlyOutMenu';
import SplitInvoiceLineItemDispensesTableList from './SplitInvoiceLineItemDispensesTableList';
import { SplitOrderInvoicesDetailLineItem } from './split_order_invoices_detail_types';
import splitOrderInvoicesRepo from '../data/splitOrderInvoicesRepo';
import { HttpErrorIgnore } from '../../../../global';
import HelpButton from '../../../core/components/HelpButton';
import SaveToExcelButton from '../../../core/table/actions/SaveToExcelButton';
import { useAtomValue } from 'jotai';
import { splitOrderInvoicesDetailPageStateAtom } from './split_order_invoices_detail_atoms';

export const splitOrderInvoiceLineItemsTableId = 'splitOrderInvoiceLineItemsList';

const SplitOrderInvoiceLineItemsTableList = (): JSX.Element => {
    const { init } = useSplitOrderInvoiceLineItemsTableInit();
    if (init) {
        return <></>;
    }
    return <TableWrapper />;
};

const TableWrapper = (): JSX.Element => {
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const { items } = useSplitOrderInvoiceLineItemsData();
    const { selectedRows, setSelectedRows } = useSelectRow();
    const invoice = useSplitOrderInvoicesDetailModel();
    const { userType, lineitemselected } = useAtomValue(splitOrderInvoicesDetailPageStateAtom);
    const { getListAsync } = useSplitInvoiceLineItemDispensesData();
    const autoOpenedRef = useRef(false);

    const { fltStats, paging, cols, sections, sort, filters, setFilter, setSort, setPaging, waiting } =
        useSplitOrderInvoiceLineItemsTableState();

    // Auto-open backing flyout on breadcrumb return
    useEffect(() => {
        if (autoOpenedRef.current || !lineitemselected?.item?.LineId || !invoice.Id || items.length === 0) return;
        autoOpenedRef.current = true;
        const lineId = lineitemselected.item.LineId;
        const matchingItem = items.find((item: any) => item.LineId === lineId);
        if (matchingItem) {
            getListAsync(invoice.Id, lineId);
            setSelectedRows([matchingItem], false, true);
            setShowDialog(true);
        }
    }, [lineitemselected, invoice.Id, items, getListAsync, setSelectedRows]);

    const handleRowClick = useCallback(
        (rowItem: any): void => {
            if (!rowItem.LineId) {
                ngDelegate.ok('Line Item Backing', 'No backing dispenses available.');
                return;
            }
            getListAsync(invoice.Id, rowItem.LineId);
            setSelectedRows([rowItem], false, true);
            setShowDialog(true);
        },
        [setSelectedRows, setShowDialog, getListAsync, invoice.Id],
    );

    const showHelp = (): void => ngDelegate.helpLink('order-splitinvoices-detail', 'Split Invoice Detail');

    const onGetExcel = (): Promise<HttpErrorIgnore | undefined> =>
        splitOrderInvoicesRepo.getBackingDispensesExcel(invoice.Id, invoice.VendorInvoiceNum ?? '', !!invoice.IsRebate);

    const handleFlyOutClose = useCallback((): void => {
        setSelectedRows([]);
        setShowDialog(false);
    }, [setSelectedRows, setShowDialog]);

    const selectedKey = Object.keys(selectedRows)[0];
    const selectedLineItem = selectedRows[selectedKey] as SplitOrderInvoicesDetailLineItem;
    const dispenseBackingDialogTitle = selectedLineItem
        ? `Line #${selectedLineItem?.LineNum} - ${selectedLineItem?.NDC}: ${selectedLineItem?.VendorItemName} Dispense Backing`
        : 'Dispense Backing';

    if (waiting) {
        return <></>;
    }

    return (
        <>
            <TableList noSpacing>
                <TableTop
                    id={splitOrderInvoiceLineItemsTableId}
                    fltStats={fltStats}
                    topRight={
                        <div>
                            <SaveToExcelButton
                                waiting={waiting}
                                setWait={(): null => null}
                                onGetExcel={onGetExcel}
                            />
                            <HelpButton onClick={showHelp} />
                        </div>
                    }
                />
                <table>
                    <TableHead
                        cols={cols}
                        sections={sections}
                        sort={sort}
                        filters={filters}
                        setFilter={setFilter}
                        setSort={setSort}
                    />
                    <TableBody
                        cols={cols}
                        rows={items}
                        onClick={handleRowClick}
                    />
                </table>
                <TablePaging
                    paging={paging}
                    setPaging={setPaging}
                    fltStats={fltStats}
                />
            </TableList>
            <FlyOutMenu
                title={dispenseBackingDialogTitle}
                isOpen={showDialog}
                onClose={handleFlyOutClose}
                width={900}
                overflowY="scroll"
            >
                <SplitInvoiceLineItemDispensesTableList
                    invoiceId={invoice.Id}
                    lineItemId={selectedLineItem?.LineId}
                    rebatesOnly={invoice.IsRebate ?? false}
                    userType={userType}
                />
            </FlyOutMenu>
        </>
    );
};

export default SplitOrderInvoiceLineItemsTableList;
